import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { ModalContainer } from '../modal-container';

import { ITariff } from '../../store/tariff.slice';

import { useAppSelector } from '../../hooks/redux';

import { startPaymentWidget } from '../../utils/cloudPaumentUtils';

import { useBreakpoint } from '../../hooks/useBreakpoint';

import { Breakpoint } from '../../utils/breakpoint';

import { IUser } from '../../store/types';

import { Button, Heading } from '../../shared/ui';

import { usePosthogCapture } from '../../hooks/usePosthogCapture';
import { useCreateCombinedOrderMutation, useGetBillingSubscriptionOverviewQuery } from '../../services/billing.api';

import BonusInput from './bonus-input';
import { TariffCardHeader } from './tariff-card-header';
import { BonusesCard } from './bunuses-card';

export interface ITariffBuyModalProps {
  tariff: ITariff;
  onClose?: (paymentDone?: boolean) => void;
}

const toIsoString = (value: Date | string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const InstantByWidget: FC<{ userData: IUser; tariff: ITariff; onSuccess: () => void; onClose: () => void }> = ({
  userData,
  tariff,
  onSuccess,
  onClose,
}) => {
  const ref = useRef(false);
  const [createCombinedOrder] = useCreateCombinedOrderMutation();
  const activeTariffs = useAppSelector((state) => state.tariffSlice.current);
  const activePaidTariffs = activeTariffs.filter((item) => item.price > 0);
  const scheduledStartAt =
    activePaidTariffs.length === 1 && activePaidTariffs[0].end_date
      ? toIsoString(activePaidTariffs[0].end_date)
      : undefined;

  useEffect(() => {
    if (!ref.current) {
      ref.current = true;
      const createOrderAndPay = async () => {
        try {
          const order = await createCombinedOrder({
            package_code: tariff.id,
            bonus_amount: 0,
            start_at: scheduledStartAt,
          }).unwrap();

          startPaymentWidget({
            planId: tariff.id,
            userId: userData.id,
            amount: order.payable_amount,
            currency: order.currency ?? tariff.currency_code,
            description: `Оплата тарифа ${tariff.name}`,
            email: userData.email,
            transactionId: order.public_order_id,
            bonusesUsed: 0,
            onSuccess: onSuccess,
            onFail: () => onClose?.(),
          });
        } catch (error) {
          onClose?.();
        }
      };

      void createOrderAndPay();
    }
  }, [
    createCombinedOrder,
    onClose,
    onSuccess,
    tariff.currency_code,
    tariff.id,
    tariff.name,
    userData.email,
    userData.id,
  ]);

  return null;
};
export const TariffBuyModal: FC<ITariffBuyModalProps> = ({ tariff, onClose }) => {
  const wallet = useAppSelector((state) => state.walletSlice);
  const userData = useAppSelector((state) => state.authSlice.userData);
  const activeTariffs = useAppSelector((state) => state.tariffSlice.current);
  const { data: overviewData } = useGetBillingSubscriptionOverviewQuery();
  const activePaidTariffs = activeTariffs.filter((item) => item.price > 0);
  const scheduledStartAt =
    activePaidTariffs.length === 1 && activePaidTariffs[0].end_date
      ? toIsoString(activePaidTariffs[0].end_date)
      : undefined;
  const breakpoint = useBreakpoint();
  const [bonusesUsed, setBonusesUsed] = useState<number | null>(null);
  const captureEvent = usePosthogCapture();
  const [createCombinedOrder] = useCreateCombinedOrderMutation();
  const [showAddonWarning, setShowAddonWarning] = useState(false);
  const [addonWarningConfirmed, setAddonWarningConfirmed] = useState(false);

  const onSuccessPayment = useCallback(() => {
    captureEvent('tariff_purchase_completed', {
      tariff_id: tariff.id,
      price: tariff.price,
      currency: tariff.currency_code,
      bonuses_used: bonusesUsed ?? 0,
    });
    onClose?.(true);
  }, [bonusesUsed, captureEvent, onClose, tariff.currency_code, tariff.id, tariff.price]);

  const bonusInputEnabled =
    tariff.is_bonus_payment_available && (wallet.wallet?.balance ?? 0) > 0 && tariff.bonus_payment_limit_percent > 0;
  const hasActiveAddonRemaining = (overviewData?.addons ?? []).some((addon) =>
    (addon.features ?? []).some((feature) => Number(feature.remaining ?? 0) > 0),
  );
  const activeSubscriptionPrice = Number(overviewData?.subscription?.package?.price_amount ?? 0);
  const shouldWarnAddonCancellation = activeSubscriptionPrice <= 0 && hasActiveAddonRemaining;

  const max = Math.min(
    wallet.wallet?.balance ?? 0,
    Math.round(tariff.price * (tariff.bonus_payment_limit_percent / 100)),
  );

  if (!userData) {
    return (
      <ModalContainer onClose={onClose}>
        <div className="flex items-center h-full">
          <p className="text-[red] text-display-s font-normal 1">Пользователь не зарегистрирован, оплата не доступна</p>
        </div>
      </ModalContainer>
    );
  }

  if (!bonusInputEnabled) {
    if (shouldWarnAddonCancellation && !addonWarningConfirmed) {
      return (
        <ModalContainer
          onClose={onClose}
          width="w-full xs:w-auto xs:max-w-[360px]"
          height="xs:max-h-[420px]"
          rounded="xs:rounded-popup"
          clickOutsideMode={breakpoint >= Breakpoint.Xs}
        >
          <WarningContent onCancel={() => onClose?.()} onConfirm={() => setAddonWarningConfirmed(true)} />
        </ModalContainer>
      );
    }

    return (
      <InstantByWidget tariff={tariff} userData={userData} onSuccess={onSuccessPayment} onClose={() => onClose?.()} />
    );
  }

  const amountToPay = tariff.price - (bonusesUsed ?? 0);

  const handlePayment = useCallback(async () => {
    try {
      const order = await createCombinedOrder({
        package_code: tariff.id,
        bonus_amount: bonusesUsed ?? 0,
        start_at: scheduledStartAt,
      }).unwrap();

      startPaymentWidget({
        planId: tariff.id,
        userId: userData.id,
        amount: order.payable_amount,
        currency: order.currency ?? tariff.currency_code,
        description: `Оплата тарифа ${tariff.name}`,
        email: userData.email,
        transactionId: order.public_order_id,
        bonusesUsed: bonusesUsed ?? 0,
        onSuccess: onSuccessPayment,
        onFail: () => onClose?.(),
      });
    } catch (error) {
      onClose?.();
    }
  }, [
    bonusesUsed,
    createCombinedOrder,
    onClose,
    onSuccessPayment,
    scheduledStartAt,
    tariff.currency_code,
    tariff.id,
    tariff.name,
    userData.email,
    userData.id,
  ]);

  return (
    <ModalContainer
      onClose={onClose}
      width="w-full xs:w-auto xs:max-w-[360px]"
      height="xs:max-h-[630px]"
      rounded="xs:rounded-popup"
      clickOutsideMode={breakpoint >= Breakpoint.Xs}
    >
      {showAddonWarning && shouldWarnAddonCancellation ? (
        <WarningContent
          onCancel={() => setShowAddonWarning(false)}
          onConfirm={() => {
            setShowAddonWarning(false);
            setAddonWarningConfirmed(true);
            void handlePayment();
          }}
        />
      ) : (
        <div className="flex flex-col gap-10 w-full h-full p-2">
          <div className="flex justify-center items-center">
            <BonusesCard />
          </div>
          <TariffCardHeader tariff={tariff} modal={true} />
          <BonusInput
            min={0}
            max={max}
            value={bonusesUsed}
            onChanged={setBonusesUsed}
            disabled={!bonusInputEnabled}
            maxPercent={tariff.bonus_payment_limit_percent}
          />
          <div className="w-full flex flex-row justify-center ">
            <Button
              size="lg"
              variant="dark"
              fullWidth
              className="lg:mt-auto"
              onClick={() => {
                if (shouldWarnAddonCancellation && !addonWarningConfirmed) {
                  setShowAddonWarning(true);
                  return;
                }

                void handlePayment();
              }}
            >
              Оплатить {amountToPay}
            </Button>
          </div>
        </div>
      )}
    </ModalContainer>
  );
};

const WarningContent = ({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) => (
  <div className="flex flex-col gap-6 p-4">
    <Heading size="md" as="h3">
      Внимание
    </Heading>
    <p className="text-ui text-ink-muted mb-4">
      При подключении нового тарифа все активные дополнения будут отменены. Продолжить?
    </p>
    <div className="flex flex-wrap justify-end gap-3">
      <Button variant="neutral" size="md" onClick={onCancel}>
        Отмена
      </Button>
      <Button variant="dark" size="md" onClick={onConfirm}>
        Продолжить
      </Button>
    </div>
  </div>
);
